import React, { memo } from 'react';
import { StyleSheet, View } from 'react-native';
import { PortalProvider } from '@gorhom/portal';
import { theme } from '@sweetgreen/garnish';

import { ErrorFallback } from '@order/components';
import { useCustomerTelemetry } from '@order/Customer';
import {
  useFetchUserCart,
  useNetworkSubscription,
  useSession,
} from '@order/hooks';
import { useTrackLastInteractedStore } from '@order/LastInteractedStore';

import {
  LoadingAnimation,
  useResetDietaryRestrictionsOnLogout,
} from './components';
import { use3rdPartyIntegrations } from './hooks';
import { useOrderAppFonts } from './hooks/useOrderAppFonts';
import { useFacebookPixel } from './integrations/facebook-pixel';
import { useOneTrust } from './integrations/onetrust';
import { AppNavigation } from './navigation/AppNavigation';
import { useEmptyPageTabNavigationFix } from './navigation/AppNavigation.hooks';
import { useMaintenanceMode } from './screens';

// ────────────────────────────────────────────────────────────────────────────────

export const App = memo(() => {
  const { sessionReady, sessionError } = useSession();
  const [appFontsLoaded] = useOrderAppFonts();

  const isLoading = !appFontsLoaded || !sessionReady;
  const containerStyle = [StyleSheet.absoluteFill, styles.appContainer];

  useMaintenanceMode();
  use3rdPartyIntegrations();
  useCustomerTelemetry();
  useFetchUserCart();
  useNetworkSubscription();
  useOneTrust();
  useEmptyPageTabNavigationFix();
  useResetDietaryRestrictionsOnLogout();
  useFacebookPixel();
  useTrackLastInteractedStore();

  if (sessionError) return <ErrorFallback error={sessionError} />;

  if (isLoading)
    return (
      <LoadingAnimation
        withNavbarOffset
        backgroundColor={theme.colors.OATMEAL}
      />
    );

  return (
    <View style={containerStyle}>
      <PortalProvider>
        <AppNavigation />
      </PortalProvider>
    </View>
  );
});

// ─── Styles ────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  appContainer: {
    backgroundColor: theme.colors.OATMEAL,
  },
});
